import { MerchantGiftCardBalanceCheckQuery, useMerchantGiftCardBalanceCheckLazyQuery } from '@/__generated__/types';
import LoadingComponent from '@/components/LoadingComponent';
import { useSnackbarHelper } from '@/components/useSnackbarHelper';
import { fCurrency } from '@/utils/formatNumber';
import { fDateTimeTimestamp } from '@/utils/formatTime';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContactlessOutlinedIcon from '@mui/icons-material/ContactlessOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LoadingButton from '@mui/material/Button';
import { Alert, Box, Card, CardContent, Divider, Stack, TextField, Typography, alpha, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MerchantGiftCardContactField from './components/MerchantGiftCardContactField';
import useMerchantGiftCardOtp from './hooks/useMerchantGiftCardOtp';

interface MerchantGiftCardBalanceProps {
    merchantId?: string;
}

type UserMerchantGiftCardActivity = NonNullable<
    NonNullable<MerchantGiftCardBalanceCheckQuery['merchantGiftCardBalanceCheck']>['activities']
>[number];

const MerchantGiftCardBalance = ({ merchantId }: MerchantGiftCardBalanceProps): JSX.Element => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbarHelper();

    const [isVerified, setIsVerified] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);

    const {
        contactMethod,
        setContactMethod,
        email,
        setEmail,
        phone,
        setPhone,
        phoneCountry,
        setPhoneCountry,
        identifier,
        identifierLabelKey,
        verificationCode,
        setVerificationCode,
        otpId,
        isCodeSent,
        isSendingCode,
        resendCountdown,
        sendOtp,
        resetOtpFlow
    } = useMerchantGiftCardOtp({ merchantId });

    const [merchantGiftCardBalanceCheckQuery, { data, loading, error }] = useMerchantGiftCardBalanceCheckLazyQuery();

    const account = data?.merchantGiftCardBalanceCheck;
    const activities = useMemo(() => [...(account?.activities || [])].sort((a, b) => b.createdAt - a.createdAt), [account?.activities]);
    const lastUpdatedAt = useMemo(() => {
        const latestUpdatedAt = activities.reduce<number | null>((latest, activity) => {
            const currentUpdatedAt = activity.updatedAt ?? null;
            if (currentUpdatedAt === null) {
                return latest;
            }
            return latest === null || currentUpdatedAt > latest ? currentUpdatedAt : latest;
        }, null);

        return latestUpdatedAt ?? activities[0]?.createdAt;
    }, [activities]);

    const formatCurrency = (amountInCents?: number | null): string => fCurrency((amountInCents || 0) / 100);

    const handleSendVerificationCode = async (): Promise<void> => {
        const isSuccess = await sendOtp();
        if (isSuccess) {
            setIsVerified(false);
        }
    };

    const handleVerifyCode = async (): Promise<void> => {
        const otp = verificationCode.trim();

        if (!otp) {
            showSnackbar(t('giftCard.pleaseEnterCode'), 'error');
            return;
        }
        if (!merchantId || !otpId) {
            showSnackbar(t('giftCard.invalidCode'), 'error');
            return;
        }

        setIsVerifying(true);
        try {
            const { data: checkBalanceData } = await merchantGiftCardBalanceCheckQuery({
                variables: {
                    otpId,
                    otp,
                    merchantId
                },
                fetchPolicy: 'no-cache'
            });

            if (!checkBalanceData?.merchantGiftCardBalanceCheck) {
                showSnackbar(t('giftCard.invalidCode'), 'error');
                return;
            }

            setIsVerified(true);
            showSnackbar(t('giftCard.verificationSuccessful'), 'success');
        } catch {
            showSnackbar(t('giftCard.invalidCode'), 'error');
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResetVerification = (): void => {
        setIsVerified(false);
        resetOtpFlow();
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 480, mx: 'auto', p: { xs: 2, sm: 3 } }}>
            {!isVerified && (
                <Card sx={{ borderRadius: 2, boxShadow: theme.customShadows.z8 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack spacing={2.5}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {t('giftCard.verifyYourIdentity')}
                            </Typography>

                            <Stack spacing={1}>
                                <MerchantGiftCardContactField
                                    contactMethod={contactMethod}
                                    onContactMethodChange={setContactMethod}
                                    email={email}
                                    onEmailChange={setEmail}
                                    phone={phone}
                                    onPhoneChange={setPhone}
                                    phoneCountry={phoneCountry}
                                    onPhoneCountryChange={setPhoneCountry}
                                    disabled={isCodeSent}
                                />
                                <LoadingButton
                                    variant={isCodeSent ? 'outlined' : 'contained'}
                                    loading={isSendingCode}
                                    onClick={handleSendVerificationCode}
                                    fullWidth
                                    size="large"
                                    disabled={resendCountdown > 0}
                                >
                                    {isCodeSent ? t('giftCard.resendIn', { seconds: resendCountdown }) : t('giftCard.sendCode')}
                                </LoadingButton>
                            </Stack>

                            {isCodeSent && (
                                <>
                                    <Divider />
                                    <Stack spacing={1}>
                                        <TextField
                                            fullWidth
                                            label={t('giftCard.verificationCode')}
                                            value={verificationCode}
                                            onChange={(e) => setVerificationCode(e.target.value)}
                                            placeholder={t('giftCard.enterCode')}
                                            onKeyDown={(e) => e.key === 'Enter' && handleVerifyCode()}
                                        />
                                        <LoadingButton
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                            loading={isVerifying}
                                            onClick={handleVerifyCode}
                                        >
                                            {t('giftCard.verify')}
                                        </LoadingButton>
                                    </Stack>
                                </>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {isVerified && (
                <>
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <LoadingComponent />
                        </Box>
                    )}

                    {!loading && error && (
                        <Alert severity="error" sx={{ borderRadius: 1 }}>
                            {t('giftCard.loadError')}
                        </Alert>
                    )}

                    {!loading && !error && !account && (
                        <Box sx={{ textAlign: 'center', py: 5 }}>
                            <CardGiftcardIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                            <Typography variant="subtitle1" color="text.secondary">
                                {t('giftCard.noGiftCard')}
                            </Typography>
                        </Box>
                    )}

                    {!loading && !error && account && (
                        <Stack spacing={3}>
                            {/* Bank Card Graphic */}
                            <Box
                                sx={{
                                    borderRadius: 3,
                                    p: 3,
                                    color: 'common.white',
                                    background: `linear-gradient(135deg, #1A1A1A 0%, #333333 100%)`, // Dark, sleek card background
                                    position: 'relative',
                                    overflow: 'hidden',
                                    boxShadow: '0 12px 24px -4px rgba(0, 0, 0, 0.2)',
                                    aspectRatio: '1.58/1',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between'
                                }}
                            >
                                {/* Background glow effect */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: -50,
                                        right: -50,
                                        width: 150,
                                        height: 150,
                                        borderRadius: '50%',
                                        background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.4)} 0%, transparent 70%)`
                                    }}
                                />

                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ zIndex: 1 }}>
                                    <ContactlessOutlinedIcon sx={{ fontSize: 32, transform: 'rotate(90deg)', opacity: 0.8 }} />
                                    <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.7 }}>
                                        {t('giftCard.title')}
                                    </Typography>
                                </Stack>

                                <Box sx={{ zIndex: 1 }}>
                                    <Typography variant="overline" sx={{ opacity: 0.7, letterSpacing: 1 }}>
                                        {t('giftCard.availableBalance')}
                                    </Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 600, mt: 0.5, letterSpacing: 0.5 }}>
                                        {formatCurrency(account.balance)}
                                    </Typography>
                                </Box>

                                <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ zIndex: 1 }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                                            {t(identifierLabelKey)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500, letterSpacing: 1 }}>
                                            {identifier}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                                            {t('giftCard.lastUpdated')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {lastUpdatedAt ? fDateTimeTimestamp(lastUpdatedAt) : '-'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>

                            {/* Verification status / Switch Account */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 2,
                                    py: 1.5,
                                    borderRadius: 2,
                                    bgcolor: alpha(theme.palette.success.main, 0.08)
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                    <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 600 }}>
                                        {t('giftCard.identityVerified')}
                                    </Typography>
                                </Stack>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    onClick={handleResetVerification}
                                >
                                    {t('giftCard.reVerify')}
                                </Typography>
                            </Box>

                            {/* Transaction History (List View for Mobile) */}
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                    {t('giftCard.transactionHistory')}
                                </Typography>

                                {activities.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'background.neutral', borderRadius: 2 }}>
                                        <ReceiptLongIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {t('giftCard.noTransactions')}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Stack spacing={1.5}>
                                        {activities.map((activity: UserMerchantGiftCardActivity) => {
                                            const isCredit = activity.type?.toUpperCase() === 'CREDIT';

                                            return (
                                                <Box
                                                    key={activity.id}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                        bgcolor: 'background.paper',
                                                        boxShadow: theme.customShadows.z1,
                                                        border: `1px solid ${theme.palette.divider}`
                                                    }}
                                                >
                                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                                        <Box
                                                            sx={{
                                                                width: 40,
                                                                height: 40,
                                                                borderRadius: '50%',
                                                                bgcolor: isCredit
                                                                    ? alpha(theme.palette.success.main, 0.1)
                                                                    : alpha(theme.palette.text.primary, 0.05),
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            {isCredit ? (
                                                                <ArrowDownwardIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                                            ) : (
                                                                <ArrowUpwardIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                            )}
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="subtitle2">{activity.type || '-'}</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {fDateTimeTimestamp(activity.createdAt)}
                                                            </Typography>
                                                            {activity.description && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.disabled"
                                                                    sx={{ display: 'block' }}
                                                                >
                                                                    {activity.description}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Stack>
                                                    <Box textAlign="right">
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: isCredit ? 'success.main' : 'text.primary'
                                                            }}
                                                        >
                                                            {isCredit ? '+' : '-'}
                                                            {formatCurrency(activity.amount)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                )}
                            </Box>
                        </Stack>
                    )}
                </>
            )}
        </Box>
    );
};

export default MerchantGiftCardBalance;
